import {
  fetchQuery,
  graphql,
  useRelayEnvironment,
  Environment,
} from "react-relay";
import Plugin, { SUGGESTION_LIST_LENGTH_LIMIT } from "./Plugin";
import { SuggestionEntitiesQuery as SuggestionEntitiesQueryType } from "./__generated__/SuggestionEntitiesQuery.graphql";
import { IntlShape, useIntl } from "react-intl";
import { SuggestionMemberQuery as SuggestionMemberQueryType } from "./__generated__/SuggestionMemberQuery.graphql";
import ItemSuggestion from "./ItemSuggestion";
import { ItemSuggestionEntityFragment$key } from "./__generated__/ItemSuggestionEntityFragment.graphql";
import { Trigger } from "./type";

const SUGGESTION_THRESHOLD = 2;

const SuggestionEntitiesQuery = graphql`
  query SuggestionEntitiesQuery($count: Int, $search: String) {
    entities(first: $count, search: $search) {
      edges {
        node {
          key: username
          value: displayName
          ...ItemSuggestionEntityFragment
        }
      }
    }
  }
`;

const SuggestionsMemberQuery = graphql`
  query SuggestionMemberQuery($id: ID!, $count: Int, $search: String) {
    node(id: $id) {
      ... on Competition {
        suggestions: members(first: $count, search: $search) {
          edges {
            node {
              entity {
                key: username
                value: displayName
                ...ItemSuggestionEntityFragment
              }
            }
          }
        }
      }
      ... on Event {
        suggestions: members(first: $count, search: $search) {
          edges {
            node {
              entity {
                key: username
                value: displayName
                ...ItemSuggestionEntityFragment
              }
            }
          }
        }
      }
    }
  }
`;

const fetchSuggestionEntities = async (
  environment: Environment,
  intl: IntlShape,
  search?: string,
) => {
  const query = await fetchQuery<SuggestionEntitiesQueryType>(
    environment,
    SuggestionEntitiesQuery,
    { search, count: SUGGESTION_LIST_LENGTH_LIMIT },
    { fetchPolicy: "store-or-network" },
  ).toPromise();
  if (!query) {
    throw Error(
      intl.formatMessage({
        defaultMessage: "No response received",
      }),
    );
  }
  return query.entities.edges;
};

const fetchSuggestionMembers = async (
  id: ID,
  environment: Environment,
  intl: IntlShape,
  search?: string,
) => {
  const query = await fetchQuery<SuggestionMemberQueryType>(
    environment,
    SuggestionsMemberQuery,
    { id, search, count: SUGGESTION_LIST_LENGTH_LIMIT },
    { fetchPolicy: "store-or-network" },
  ).toPromise();
  if (!query) {
    throw Error(
      intl.formatMessage({
        defaultMessage: "No response received",
      }),
    );
  }
  return query.node.suggestions?.edges ?? [];
};

function Entities() {
  const relayEnvironement = useRelayEnvironment();
  const intl = useIntl();

  return (
    <Plugin
      onSearch={async (search) => ({
        "@": await fetchSuggestionEntities(relayEnvironement, intl, search),
      })}
      renderItems={{
        "@": (item, props) => (
          <ItemSuggestion.Entity key={item.key} option={item} {...props} />
        ),
      }}
    />
  );
}

interface SuggestionsMemberProps {
  id: ID;
  fallback?: boolean;
}

function Members({ id, fallback = true }: SuggestionsMemberProps) {
  const relayEnvironement = useRelayEnvironment();
  const intl = useIntl();

  return (
    <Plugin<Record<Trigger, ItemSuggestionEntityFragment$key>>
      onSearch={async (search) => {
        const memebersSuggestions = await fetchSuggestionMembers(
          id,
          relayEnvironement,
          intl,
          search,
        );
        const shouldFallbackToAllSuggestions =
          fallback && memebersSuggestions.length <= SUGGESTION_THRESHOLD;
        return {
          "@": shouldFallbackToAllSuggestions
            ? await fetchSuggestionEntities(relayEnvironement, intl, search)
            : memebersSuggestions,
        };
      }}
      renderItems={{
        "@": (item, props) => (
          <ItemSuggestion.Entity key={item.key} option={item} {...props} />
        ),
      }}
    />
  );
}

export interface Source {
  member?: ID;
}

interface Props {
  source?: Source;
  fallback?: boolean;
}

export default function SuggestionPlugin(props: Props) {
  return props.source?.member ? (
    <Members id={props.source.member} fallback={props.fallback} />
  ) : (
    <Entities />
  );
}
